import { getDustValue } from '@exodus/bitcoin-api/src/tx-send';
import type NumberUnit from '@exodus/currency';
import { formatBalance } from '@exodus/formatting/lib/asset';
import { VALIDATION_TYPES } from '@exodus/send-validation/src/constants';
import INVALID_ADDRESS from '@exodus/send-validation/src/validations/INVALID_ADDRESS';
import createNotEnoughtOutputValidator from '@exodus/send-validation/src/validations/createNotEnoughtOutputValidator';
import {
  solanaAddressTypeValidator,
  solanaMintAddressValidator,
  solanaRentExemptAmountValidator,
  solanaRentExemptAmountSenderValidator,
} from '@exodus/send-validation/src/validations/solana';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { Asset, FeesResult } from '@/types';
import selectors from '@/ui/flux/selectors';
import useReceiveAddress from '@/ui/hooks/use-receive-address';
import type { SendValidationDependencies } from '@/ui/utils/validation';
import {
  bitcoinCPFPValidation,
  baseAssetInsufficientFundsValidation,
  createValidationHookWithDefaults,
  insufficientFundsValidation,
  zeroAmountValidation,
} from '@/ui/utils/validation';
import { ADDRESS_TYPE_VALIDATION } from '@/ui/utils/validation/solana/address-type';
import { MINT_ADDRESS_VALIDATION } from '@/ui/utils/validation/solana/mint';
import useIsBuySupportedByAssetFn from './use-is-buy-supported-by-asset-fn.js';

const addressValidation = {
  ...INVALID_ADDRESS,
  getMessage: ({ destinationAddress }) =>
    destinationAddress?.length > 0 &&
    'The address you entered is invalid. Make sure your address is correct and try again.',
};

// cannot send SOL to token Address
const addressTypeSolanaValidation = {
  ...ADDRESS_TYPE_VALIDATION,
  shouldValidate: ({ asset, destinationAddress }) =>
    asset.name === 'solana' && destinationAddress?.length > 0,
  getMessage: () => 'Destination address is for a different token type.',
};

// cannot send token X to a different target account address (token Y)
const mintAddressSolanaValidation = {
  ...MINT_ADDRESS_VALIDATION,
  getMessage: () => 'Destination address is for a different token type.',
};

const baseNotEnoughOutputBitcoinValidation = createNotEnoughtOutputValidator({
  getMinAmount: ({ asset }) => getDustValue(asset),
});
const notEnoughOutputBitcoinValidation = {
  ...baseNotEnoughOutputBitcoinValidation,
  isValid: ({ amount, ...rest }) =>
    baseNotEnoughOutputBitcoinValidation.isValid({
      ...rest,
      sendAmount: amount,
    }),
  getMessage: ({ asset }) => {
    const dustValue = getDustValue(asset);
    return `The ${asset.displayName} network doesn't allow sending amounts less than ${formatBalance(dustValue, asset)}.`;
  },
};

// TODO: Fetch asset address details
const addressDetails = {};

type UseSendValidationParams = {
  asset: Asset;
  walletAccount: string;
  toAddress: string;
  amount?: NumberUnit;
  availableBalance?: NumberUnit;
  fees?: FeesResult;
  isFeeAssetEnough?: boolean;
  isBuySupportedByAsset?: boolean;
  withAmount?: boolean;
};

const useSendValidation = ({
  asset,
  walletAccount,
  toAddress,
  amount,
  availableBalance,
  fees,
  isFeeAssetEnough = false,
  withAmount = true,
}: UseSendValidationParams) => {
  const isBuySupportedByAsset = useIsBuySupportedByAssetFn();

  const spendableBalance = useSelector(selectors.balances.getSpendable)({
    assetName: asset.name,
    walletAccount,
  });

  const { address: fromAddress } = useReceiveAddress({
    assetName: asset.baseAsset.name,
    walletAccount,
  });

  const accountState = useSelector(
    selectors.accountStates.createBaseAssetSourceSelector({
      assetName: asset.baseAsset.name,
      walletAccount,
    }),
  );

  const deps = {
    asset,
    destinationAddress: toAddress,
    addressDetails,
    amount,
    sendAmount: amount,
    availableBalance,
    isFeeAssetEnough,
    isBuySupportedByAsset,
    fees,
    spendableBalance,
    accountState,
    fromAddress,
  } satisfies SendValidationDependencies;

  const validations = useMemo(
    () =>
      [
        addressValidation,
        addressTypeSolanaValidation,
        mintAddressSolanaValidation,
        withAmount && zeroAmountValidation,
        withAmount && insufficientFundsValidation,
        baseAssetInsufficientFundsValidation,
        notEnoughOutputBitcoinValidation,
        bitcoinCPFPValidation,
        solanaAddressTypeValidator,
        solanaMintAddressValidator,
        solanaRentExemptAmountValidator,
        solanaRentExemptAmountSenderValidator,
      ].filter(Boolean),
    [withAmount],
  );
  const useValidation = useMemo(
    () =>
      createValidationHookWithDefaults<SendValidationDependencies>({
        validations,
      }),
    [validations],
  );
  const { failedValidation } = useValidation(deps);

  if (!failedValidation) {
    return {};
  }

  const action = failedValidation.getAction?.(deps);
  const message = failedValidation.message;
  if (failedValidation.type === VALIDATION_TYPES.WARN) {
    return { warning: message, action };
  }
  return { error: message, action };
};

export default useSendValidation;
